import { Link } from "react-router-dom";
import { PartyPopper, Flame, Heart, Sunrise } from "lucide-react";
import { LineReveal, Reveal } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { Burst } from "@/components/Burst";

const CHAPTERS = [
  {
    n: "01",
    title: "A Simple Idea",
    body: "Crazy Buns started with a simple idea: make breakfast that makes people smile. We love breakfast food, we love making people happy, and we believe there's a whole lot of love that goes into every bun we make.",
    bg: "bg-cb-yellow",
    numColor: "text-outline-pink",
  },
  {
    n: "02",
    title: "Comfort Food, Big Flavors",
    body: "From our warm, gooey rolls to biscuits & gravy and a bottle of coffee, Crazy Buns is all about comfort food, big flavors, and even bigger smiles.",
    bg: "bg-cb-teal",
    numColor: "text-outline-white",
  },
  {
    n: "03",
    title: "Come Hungry, Leave Happy",
    body: "Come hungry, leave happy — and don't be surprised if you become crazy about our buns!",
    bg: "bg-cb-pink text-white",
    numColor: "text-outline-yellow",
  },
];

const VALUES = [
  { icon: Sunrise, title: "Fresh Daily", desc: "Baked at sunrise, gone by noon." },
  { icon: Flame, title: "Served Hot", desc: "Straight from the trailer oven." },
  { icon: Heart, title: "Family Friendly", desc: "Smiles for every age, every stop." },
];

export default function Story() {
  return (
    <div data-testid="story-page">
      <section className="relative bg-cb-black overflow-hidden py-20 sm:py-28">
        <div className="absolute inset-0 halftone opacity-20" />
        <div className="max-w-7xl mx-auto px-4 grid lg:grid-cols-2 gap-12 items-center relative">
          <div>
            <span className="cb-badge bg-cb-teal text-cb-black mb-6 inline-block" data-testid="story-badge">Our Story</span>
            <h1 className="font-display font-bold uppercase leading-[0.95] tracking-tight text-4xl sm:text-5xl lg:text-6xl" data-testid="story-headline">
              <LineReveal
                lines={[
                  <span key="a" className="text-outline-yellow inline-block -rotate-1">How We Got</span>,
                  <span key="b" className="text-outline-pink inline-block rotate-1">This Crazy</span>,
                ]}
              />
            </h1>
            <Reveal delay={0.6}>
              <p className="mt-6 text-white/80 font-bold text-base md:text-lg leading-relaxed max-w-lg">
                One trailer. One oven. One completely unreasonable obsession with giant cinnamon rolls.
                This is the story of the brightest breakfast on wheels.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.3} className="relative">
            <div className="cb-card overflow-hidden rotate-2 hover:rotate-0 transition-transform duration-300">
              <img src="/images/story-trailer.png" alt="Crazy Buns mascot leaning out of the trailer" className="w-full aspect-square object-cover" />
            </div>
            <Burst text="Since Day One" subtext="Rolls & Smiles" color="#FFD700" className="absolute -bottom-8 -left-4 w-32 sm:w-40 animate-wiggle" rotate="-rotate-6" />
          </Reveal>
        </div>
      </section>

      <Marquee reverse bg="bg-cb-yellow" text="text-cb-black" />

      <section className="py-20 sm:py-28 bg-cb-cream halftone-light">
        <div className="max-w-5xl mx-auto px-4 space-y-10">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.n} delay={0.05}>
              <div
                data-testid={`story-chapter-${c.n}`}
                className={`cb-card cb-card-hover ${c.bg} p-8 sm:p-12 flex flex-col sm:flex-row gap-6 sm:gap-10 items-start ${i % 2 ? "rotate-1" : "-rotate-1"} hover:rotate-0`}
              >
                <span className={`font-display font-bold text-6xl sm:text-7xl leading-none ${c.numColor}`}>{c.n}</span>
                <div>
                  <h2 className="font-display font-semibold uppercase text-2xl sm:text-3xl tracking-tight">{c.title}</h2>
                  <p className="mt-3 font-bold text-base leading-relaxed opacity-80">{c.body}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-white border-y-3 border-cb-black py-16">
        <div className="max-w-7xl mx-auto px-4 grid sm:grid-cols-3 gap-6">
          {VALUES.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.1}>
              <div data-testid={`story-value-${v.title.toLowerCase().replace(/\s+/g, "-")}`} className="text-center">
                <div className={`w-16 h-16 mx-auto rounded-full border-3 border-cb-black shadow-hard-sm flex items-center justify-center ${["bg-cb-pink text-white", "bg-cb-yellow", "bg-cb-teal"][i]}`}>
                  <v.icon className="w-7 h-7" />
                </div>
                <h3 className="font-display font-semibold uppercase text-lg mt-4">{v.title}</h3>
                <p className="font-bold text-sm text-cb-black/60 mt-1">{v.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-cb-black py-20 text-center relative overflow-hidden">
        <div className="absolute inset-0 halftone opacity-20" />
        <Reveal className="relative max-w-3xl mx-auto px-4">
          <h2 className="font-display font-bold uppercase text-2xl sm:text-3xl lg:text-4xl text-white tracking-tight">
            Come Taste The <span className="text-cb-pink">Story</span>
          </h2>
          <p className="mt-4 text-white/70 font-bold text-base md:text-lg">Chapter four gets written every morning at the window.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/menu" data-testid="story-menu-btn" className="cb-btn cb-btn-yellow">See The Menu</Link>
            <Link to="/book" data-testid="story-book-btn" className="cb-btn cb-btn-pink"><PartyPopper className="w-5 h-5" /> Book The Truck</Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
